// src/review/canvas/ThreatCanvas.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { Stack, Card, Text, Group, Button, Badge } from '@mantine/core';
import { IconArrowLeft, IconArrowRight, IconPlayerSkipForward } from '@tabler/icons-react';
import { useSelector } from 'react-redux';
import { useReview } from '../context/ReviewContext';
import ThreatOverviewSection from './sections/ThreatOverviewSection';
import ThreatAssessmentSection from './sections/ThreatAssessmentSection';
import AttackSurfaceSection from './sections/AttackSurfaceSection';
import SecurityControlsSection from './sections/SecurityControlsSection';

/**
 * ThreatCanvas - Main canvas for reviewing a single security threat
 * Integrates all threat sections with navigation
 */
function ThreatCanvas({ threat, onNext, onPrevious, onSkip }) {
  const { modelId } = useReview();

  // Get edit count for this threat
  const reviewState = useSelector((state) =>
    state.reviews.byModel[modelId] || {}
  );
  const edits = reviewState.edits || {};

  // Count edits for this threat across all categories
  const threatEditCount = Object.values(edits)
    .flatMap(category => Object.values(category))
    .filter(edit => edit.itemId === threat.threatId)
    .length;

  return (
    <Stack gap="lg">
      {/* Header */}
      <Card withBorder p="md" bg="violet.0">
        <Group justify="space-between">
          <Text size="lg" fw={700}>{threat.threatScenario || 'Unnamed Threat'}</Text>
          {threatEditCount > 0 && (
            <Badge size="lg" variant="filled" color="violet">
              {threatEditCount} {threatEditCount === 1 ? 'Edit' : 'Edits'}
            </Badge>
          )}
        </Group>
      </Card>

      {/* Threat Overview */}
      <ThreatOverviewSection threat={threat} />

      {/* Threat Assessment */}
      <ThreatAssessmentSection threat={threat} />

      {/* Attack Surface Assessment */}
      <AttackSurfaceSection threat={threat} />

      {/* Security Controls */}
      <SecurityControlsSection threat={threat} />

      {/* Navigation Footer */}
      <Card withBorder p="md">
        <Group justify="space-between">
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={18} />}
            onClick={onPrevious}
            disabled={!onPrevious}
          >
            Previous Threat
          </Button>

          <Group gap="sm">
            {onSkip && (
              <Button
                variant="light"
                leftSection={<IconPlayerSkipForward size={18} />}
                onClick={onSkip}
              >
                Skip
              </Button>
            )}
            <Button
              rightSection={<IconArrowRight size={18} />}
              onClick={onNext}
            >
              Next Threat
            </Button>
          </Group>
        </Group>
      </Card>
    </Stack>
  );
}

ThreatCanvas.propTypes = {
  threat: PropTypes.shape({
    threatId: PropTypes.string.isRequired,
    threatScenario: PropTypes.string,
    category: PropTypes.string,
    threatActor: PropTypes.string,
    assessment: PropTypes.object,
    securityControls: PropTypes.array,
  }).isRequired,
  onNext: PropTypes.func.isRequired,
  onPrevious: PropTypes.func,
  onSkip: PropTypes.func,
};

ThreatCanvas.defaultProps = {
  onPrevious: null,
  onSkip: null,
};

export default ThreatCanvas;
